import React, { createContext, useEffect, useMemo, useState } from 'react'

import {
    localStorageKeys,
    modalActions,
    recommendedProductsListName,
    EXTEND_SKU
} from '@/config/globals'

import { getVariants } from '@/services/catalog'
import { storageWrapper } from '@saatva-bits/pattern-library.utils.storage'

import {
    pushConfigureItem,
    pushAddToSaveForLater,
    pushCartContentsUpdate,
    pushRecoStripVisible
} from '@/utils/analytics'
import { canBeABundle, pillowBundleSkus, transformProductDataForDiscountsData, sortDiscountsByType} from '@/utils/product'
import { getQueryParamsObject } from '@/utils/url'

import { useSharedCartService } from '@saatva-bits/pattern-library.modules.cart'
import { filterOutRecommendedProducts, useRecoStripService } from '@saatva-bits/pattern-library.modules.reco-strip'
import { getCookie } from '@/utils/cookies'
import { useFeatureFlag } from '@saatva-bits/pattern-library.modules.launch-darkly'
import { getDeliveryEstimate } from '@/services/cart-checkout'
import { formatDeliveryWindow } from '@/utils/order'
import { usePromotionsData, useGetDiscounts } from '@saatva-bits/pattern-library.modules.promotions'

const initialState = {
    allRecommendedProducts: null,
    isLoadingRecommendedProducts: true,
    savedForLater: [],
    selectedProduct: null,
    modalAction: modalActions.ADD,
    isConfiguringItem: false,
    sliderInitialLoad: false,
    deliveryWindow: null
}

const SAATVA_URL = process.env.NEXT_PUBLIC_SAATVA_DOMAIN

export const CartContext = createContext(initialState)

export const CartProvider = ({ children }) => {
    const sharedCart = useSharedCartService()
    const recoStrip = useRecoStripService()
    const { getDiscounts } = useGetDiscounts()
    const promotionsData = usePromotionsData()
    const sitewidePromo = promotionsData.sitewidePromo
    const [allRecommendedProducts, setAllRecommendedProducts] = useState(
        initialState.allRecommendedProducts
    )
    const [savedForLater, setSavedForLater] = useState(initialState.savedForLater)
    const [isLoadingRecommendedProducts, setIsLoadingRecommendedProducts] = useState(initialState.isLoadingRecommendedProducts)
    const [selectedProduct, setSelectedProduct] = useState(initialState.selectedProduct)
    const [modalAction, setModalAction] = useState(initialState.modalAction)
    const [cartStatus, setCartStatus] = useState(true)
    const [initialLoading, setInitialLoading] = useState(true)
    const [isConfiguringItem, setIsConfiguringItem] = useState(initialState.isConfiguringItem)
    const { isOn: extendPostPurchaseEnabled } = useFeatureFlag('ENABLE_EXTEND_POST_PURCHASE.CONV-274')
    const [detailModalIsOpen, setDetailModalIsOpen] = useState(false)
    const [detailModalVersion, setDetailModalVersion] = useState(null)
    const [sliderInitialLoad, setSliderInitialLoad] = useState(initialState.sliderInitialLoad)
    const [deliveryWindow, setDeliveryWindow] = useState(initialState.deliveryWindow)
    const storage = storageWrapper()

    const updateSavedForLater = (newSavedForLater) => {
        // Save in state
        setSavedForLater(newSavedForLater)
        // Save in localstorage
        storage.setItem(
            localStorageKeys.savedForLater,
            newSavedForLater.map( item => ({
                sku: item.sku,
                quantity: item.quantity,
                discounts: item.discounts,
                assets: item.assets,
                bundleOptions: item.bundleOptions
            })),
            'local'
        )
    }

    const loadSavedForLater = async () => {
        const data = storage.getItem(localStorageKeys.savedForLater, 'local')

        if (data && data.length) {
            const skus = data.map(item => {
                return item.sku
            })
            let variants = await getVariants(skus)

            variants = variants.map(v => {
                const item = data.find(s => s.sku === v.sku)
                return {
                    ...v,
                    productAttributes: v.attributes.map(a => {
                        return {
                            name: a.label,
                            value: a.value
                        }
                    }),
                    quantity: item.quantity,
                    discounts: item.discounts,
                    assets: item.assets,
                    bundleOptions: item.bundleOptions
                }
            })
            setSavedForLater(variants)
        }
    }

    const addSavedForLater = async (item) => {
        try {
            let newSavedForLater = []
            // Check if item is already in saved for later state
            const saveForLaterIndex = savedForLater.findIndex(productItem => productItem.sku === item.sku)

            if (saveForLaterIndex > -1) {
                const newQuantity = savedForLater[saveForLaterIndex].quantity + item.quantity
                newSavedForLater = await updateSavedForLaterItems(item, newQuantity, saveForLaterIndex)
            } else {
                newSavedForLater = [...savedForLater, item]
            }

            pushAddToSaveForLater(item)
            updateSavedForLater(newSavedForLater)
        } catch (error) {
            console.error(error)
        }
    }

    const removeSavedForLater = (skus) => {
        try {
            const newSavedForLater = savedForLater.filter(product => !skus.includes(product.sku))
            updateSavedForLater(newSavedForLater)
        } catch (error) {
            console.error(error)
        }
    }

    const updateSavedForLaterItems = async (item, quantity, savedForLaterIndex) => {
        try {
            const canBundle = canBeABundle(item) && quantity > 1
            const pillowBundleQuantity = Math.floor(quantity / 2)
            const singlePillowQuantity = quantity % 2
            let pillowBundleItem = null

            if (canBundle) {
                const variants = await getVariants([pillowBundleSkus[item.sku]])
                pillowBundleItem = variants[0]
                if (pillowBundleItem) {
                    pillowBundleItem = {
                        ...pillowBundleItem,
                        productAttributes: pillowBundleItem.attributes.map(a => {
                            return {
                                name: a.label,
                                value: a.value
                            }
                        }),
                        discounts: []
                    }
                }
            }

            // Item to update
            const itemToUpdate = savedForLater[savedForLaterIndex]
            // New saved for later list
            const newSavedForLater = [...savedForLater]
            // Remove item to update
            newSavedForLater.splice(savedForLaterIndex, 1)

            if (canBundle) {
                if (singlePillowQuantity > 0) {
                    // Update pillow
                    newSavedForLater.splice(savedForLaterIndex, 0, {...itemToUpdate,  quantity: singlePillowQuantity})
                }
                // Update bundle
                newSavedForLater.splice(savedForLaterIndex, 0, {...pillowBundleItem, quantity: pillowBundleQuantity})
            } else {
                // Update the item quantity
                newSavedForLater.splice(savedForLaterIndex, 0, { ...itemToUpdate, quantity })
            }

            return newSavedForLater
        } catch (error) {
            console.error(error)
            return []
        }
    }

    const configureItem = async({ itemId, newProduct, newItemSku, newProductQuantity, quantity, freeProducts, productBeingUpdated }) => {
        setIsConfiguringItem(true)
        const originalQty = productBeingUpdated.originalQuantity || productBeingUpdated.quantity
        
        try {
            const oldItem = sharedCart.cart.items.find(({ id }) => id === itemId) // Original cart item.
            const removeItem = oldItem.quantity === (productBeingUpdated.quantity - originalQty) // If the cart item quantity is equal to the selected product quantity then they are the same and the item can be removed fully
            const isIncrease = oldItem.quantity < quantity

            if (oldItem.sku === newItemSku && quantity > 0) {
                await sharedCart.updateItemInCart(oldItem, quantity, isIncrease ? 'increase' : 'decrease')
            }
            if (oldItem.sku !== newItemSku && quantity > 0) {
                setIsConfiguringItem(true)
                const updateQty = isIncrease ? (oldItem.quantity - productBeingUpdated.quantity) : (oldItem.quantity - newProductQuantity)
                removeItem
                    ? await sharedCart.removeItemFromCart(oldItem, 'configureItem', false)
                    : await sharedCart.updateItemInCart(oldItem, updateQty, 'decrease') // If the cart item qty does not equal the selected product qty then it is a cloned item. The original cart qty should be decreased by the cloned item qty being updated.
                await sharedCart.addItemToCart(
                    [
                        {
                            sku: newItemSku,
                            quantity: newProductQuantity,
                            bundleOptions: newProduct.bundleOptions || null,
                            name: newProduct.genericName || newProduct.name,
                            category: newProduct.category,
                            subcategory: newProduct.subcategory,
                            price: newProduct.price,
                            attributes: newProduct.attributes || newProduct.productAttributes
                        }
                    ],
                    'configureItem',
                    'cart'
                )
                // Add the protection plan again if needed
                if (oldItem.warranty) {
                    const { planId, price, term, title } = oldItem.warranty
                    const warrantyItem = {
                        offerPlanId: '',
                        planId,
                        price,
                        term: parseInt(term),
                        title,
                        product: newItemSku
                    }
                    await sharedCart.addWarrantyToItem(warrantyItem,oldItem.name, null, quantity)
                }
            }

            if (freeProducts && freeProducts.length) {
                const productsToAdd = []

                freeProducts.forEach((freeProduct) => {
                    const { freeProductSku } = freeProduct
                    productsToAdd.push(
                        {
                            sku: freeProductSku,
                            quantity: 1
                        }
                    )
                })
                await sharedCart.addItemToCart(
                    productsToAdd,
                    'configureItem',
                    'cart'
                )
            }

            const newPrice = quantity > 0 ? newProduct.price : 0

            // Analytics
            pushConfigureItem(newProduct, oldItem.price, newPrice)
            sharedCart.setIsLoading(false)
            setIsConfiguringItem(false)
        } catch (error) {
            console.error(`An error occurred while configuring the product from your cart: ${error.message}`)
            sharedCart.setIsLoading(false)
            setIsConfiguringItem(false)
        }
    }

    // Filter recommended products on cart updates
    const recommendedProducts = useMemo(() => {
        const sharedCartData = sharedCart.cart
        try {
            return filterOutRecommendedProducts(allRecommendedProducts, sharedCartData)
        } catch (error) {
            console.error(error)
            setIsLoadingRecommendedProducts(false)
            return []
        }
    }, [sharedCart.cart, allRecommendedProducts])

    useEffect(() => {
        if (sharedCart.cartId) {
            const getRecommendedProducts = async () => {
                try {
                    setIsLoadingRecommendedProducts(true)
                    await recoStrip.getAllRecommendedProducts(recommendedProductsListName)
                } catch (error) {
                    console.error(error)
                } finally {
                    setIsLoadingRecommendedProducts(false)
                }
            }

            const getSavedForLater = async () => {
                await loadSavedForLater()
            }

            getRecommendedProducts()
            getSavedForLater()
        }
    }, [sharedCart.cartId])

    useEffect(() => {
        const queryParams = getQueryParamsObject()

        if (!sharedCart.cartId && queryParams && queryParams.products) {
            sharedCart.createCart()
        }
    }, [])

    useEffect(()=> {
        const queryParams = getQueryParamsObject()

        const addCartProductsFromParams = async (productsToAdd) => {
            await sharedCart.addItemToCart(productsToAdd, null, 'cart')
        }

        if (sharedCart.cartId && queryParams && queryParams.products) {
            const productsToAdd = []
            Object.keys(queryParams.products).forEach(oneProduct => {
                productsToAdd.push({
                    sku: oneProduct,
                    quantity: Number(queryParams.products[oneProduct])
                })
            })
            addCartProductsFromParams(productsToAdd)
        }
    }, [sharedCart.cartId])

    useEffect(() => {
        sharedCart.getCart()
    }, [sharedCart.cartId])

    useEffect(() => {
        if (sharedCart.cart){
            if (cartStatus) {
                sharedCart.sendAnalyticsEvent('view_cart')
                setCartStatus(false)
            }
        }
        if (!getCookie('cartId') && initialLoading) setInitialLoading(false)
        if (initialLoading && !cartStatus) setInitialLoading(false)
    }, [sharedCart.cart, cartStatus])

    useEffect(() => {
        /**
         * Get delivery estimate data from service-cart-checkout by shipping address
         */
        const getDeliveryEstimateData = async () => {
            const { postcode, region } = sharedCart.cart.shippingAddress[0]
            const { start_window: startWindow, end_window: endWindow } = await getDeliveryEstimate(
                postcode,
                region.code
            )
            const deliveryWindow = formatDeliveryWindow(startWindow, endWindow)
            setDeliveryWindow(deliveryWindow)
        }

        if (sharedCart.cartId && sharedCart.cart && !!sharedCart.cart.items) {
            pushCartContentsUpdate(sharedCart.cart)

            if (!deliveryWindow && !!sharedCart.cart.shippingAddress?.length) {
                getDeliveryEstimateData()
            }
        }
    }, [sharedCart.cartId, sharedCart.cart, deliveryWindow])

    useEffect(() => {
        if (recoStrip.allRecommendedProducts && !!recoStrip.allRecommendedProducts.length) {
            setAllRecommendedProducts(recoStrip.allRecommendedProducts)
        }
    }, [recoStrip.allRecommendedProducts])

    useEffect(() => {
        if (sliderInitialLoad) {
            pushRecoStripVisible(1)
        }
    }, [sliderInitialLoad])

    // Redirect to home if the user has an Extend post-purchase item
    // and the feature flag is off
    useEffect(() => {
        if (sharedCart.cart && sharedCart.cart.items) {
            const hasExtendPostPurchaseItem = sharedCart.cart.items.some(
                (item) => item.sku === EXTEND_SKU && !!item.leadToken
            )
            if (hasExtendPostPurchaseItem && !extendPostPurchaseEnabled) {
                sharedCart.clearCartCookies()
                window.location.replace(SAATVA_URL)
            }
        }
    }, [extendPostPurchaseEnabled, sharedCart.cart])

    const discountsSummary = useMemo(() => {
        if (sharedCart?.cart?.items && sitewidePromo) {
            const transformedData = transformProductDataForDiscountsData(sharedCart.cart?.items) // Transforms product data to be used by the Promotion service.
            const promoServiceLabelBreakdown = getDiscounts(transformedData, { roundDiscount: false }) // Gets the discount breakdown from the Promotion service.
            const sortedDiscountLabels = sortDiscountsByType(promoServiceLabelBreakdown, sitewidePromo, sharedCart.cart?.priceDiscounts) // Sorts the discount labels into sitewide, coupon,and sku level discounts.
            return {
                ...promoServiceLabelBreakdown, 
                ...sortedDiscountLabels, 
                cartDiscounts: sharedCart.cart?.priceDiscounts,
                totalActiveDiscounts: sharedCart.cart?.priceDiscounts.length,
            }
        }
        return {}
    }, [sharedCart.cart])

    return (
        <CartContext.Provider
            value={{
                ...sharedCart,
                recommendedProducts,
                isLoadingRecommendedProducts,
                setIsLoadingRecommendedProducts,
                configureItem,
                savedForLater,
                addSavedForLater,
                removeSavedForLater,
                selectedProduct,
                setSelectedProduct,
                modalAction,
                setModalAction,
                initialLoading,
                isConfiguringItem,
                detailModalIsOpen,
                setDetailModalIsOpen,
                setSliderInitialLoad,
                detailModalVersion,
                setDetailModalVersion,
                deliveryWindow,
                discountsSummary
            }}
        >
            {children}
        </CartContext.Provider>
    )
}
